import { Stack } from '@cast/design-system';

import { ComputeResourceType } from '@cast/shared-types';

import {
  computeResourceUnitsMap,
  computeResourceWordMap,
} from 'constants/maps';

import { ChartWidget } from '../ChartWidget';

import { ChartWithPrediction } from './ChartWithPrediction';
import { useWorkloadSelectedContainer } from '../_hooks/useWorkloadSelectedContainer';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import {
  nonPredictionStrokeDasharrayMap,
  percentileToColor,
  percentileToTitle,
} from '../constants';
import {
  CurrentRequestMetricBox,
  RecommendedMetricBox,
  RecommendedRequestMetricBoxes,
} from '../RecommendedRequestMetricBoxes';
import { UsageMetricsBoxes } from '../UsageMetricsBoxes';
import { useCpuChartData } from './useCpuChartData';

export const CpuChartSection = () => {
  const { timeZone } = useRightsizingTimeRangeSelection();
  const { container } = useWorkloadSelectedContainer();
  const {
    workload,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useCpuChartData({ container, timeZone });

  return (
    <ChartWidget
      testId="cpu-chart"
      title="CPU autoscaling"
      chartEl={
        <ChartWithPrediction
          timeZone={timeZone}
          unit="CPU"
          analyzeDescription="VPA cpu metric with prediction"
          data={chartData?.chartData ?? []}
          dataKeys={chartData?.chartDataKeys ?? []}
          colorMap={percentileToColor}
          tooltipTitleMap={percentileToTitle}
          strokeDasharrayMap={
            chartData?.isReadOnly ? nonPredictionStrokeDasharrayMap : {}
          }
          isLoading={isFetching || !workload || !chartData}
          hasError={!!error && !isFetching}
          refetch={refetch}
          isMetricDisabled={isMetricDisabled}
          dataKeysRight={chartData?.hasStall ? ['avgStall'] : []}
        />
      }
      primaryMetricsEl={
        chartData && workload ? (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <RecommendedRequestMetricBoxes
              showArrow
              currentEl={
                <CurrentRequestMetricBox
                  title={`${computeResourceWordMap[ComputeResourceType.CPU]} ${
                    percentileToTitle.req
                  }`}
                  acronym={computeResourceUnitsMap[ComputeResourceType.CPU]}
                  markerColor={percentileToColor.req}
                  from={chartData.requested}
                  to={chartData.recommended}
                  isDisabled={isMetricDisabled('req')}
                  onClick={() => toggleMetric('req')}
                />
              }
              recommendationEl={
                <RecommendedMetricBox
                  type={ComputeResourceType.CPU}
                  title={`${computeResourceWordMap[ComputeResourceType.CPU]} ${
                    percentileToTitle.rec
                  }`}
                  acronym={computeResourceUnitsMap[ComputeResourceType.CPU]}
                  markerColor={percentileToColor.rec}
                  recommendation={workload.recommendation}
                  from={chartData.requested}
                  to={chartData.recommended}
                  isReadOnly={chartData.isReadOnly}
                  resourceConfig={workload.workloadConfigV2.vpaConfig.cpu}
                  isDisabled={isMetricDisabled('rec')}
                  onClick={() => toggleMetric('rec')}
                />
              }
            />
          </Stack>
        ) : null
      }
      secondaryMetricsEl={
        chartData ? (
          <UsageMetricsBoxes
            unit="CPU"
            metricsBoxes={['max', 'p75', 'p50', 'min']}
            titleMap={percentileToTitle}
            colorMap={percentileToColor}
            summary={chartData.aggregated}
            secondaryMetricsBoxes={
              chartData.hasStall ? ['avgStall'] : undefined
            }
            secondaryMetricsBoxesUnit="%"
            toggleMetricsBox={toggleMetric}
            isMetricDisabled={isMetricDisabled}
            fullWidth
          />
        ) : undefined
      }
    />
  );
};
